import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AbrirChamadoPage } from '@/portal/AbrirChamadoPage';
import { ArtigoPage } from '@/portal/ArtigoPage';
import { BasePage } from '@/portal/BasePage';
import { ChamadoPage } from '@/portal/ChamadoPage';
import { ChamadosPage } from '@/portal/ChamadosPage';
import { LoginPage } from '@/portal/LoginPage';
import { PortalShell } from '@/portal/PortalShell';
import { usePortalAuth } from '@/portal/auth.store';

/**
 * O roteador do portal.
 *
 * Arquivo próprio, e não um ramo de `@/app/router.tsx`. Aquele é o roteador da
 * **operação**, e importá-lo puxaria para dentro do bundle do portal a árvore
 * inteira de rotas administrativas — os caminhos, os nomes de campo dos
 * formulários e, em alguns casos, as próprias URLs de API que só o operador
 * deveria conhecer. Um `if (user.isInternal)` no React não desfaz isso: o
 * arquivo `.js` já foi baixado pelo cliente e é legível.
 *
 * `portal-bundle.spec.ts` percorre o grafo de imports a partir de
 * `@/portal/main.tsx` e reprova o build se ele alcançar `@/app/**` ou
 * `@/features/**`.
 *
 * ## Rotas
 *
 *   `/`               lista dos chamados do contato
 *   `/chamados/:id`   um chamado, linha do tempo pública, prazos, anexos
 *   `/abrir`          abertura pelo catálogo filtrado por contrato
 *   `/base`           artigos publicados e problemas conhecidos
 *   `/base/:id`       detalhe de um artigo
 *   `/entrar`         a porta
 */

const rotaRaiz = createRootRoute({ component: Outlet });

const rotaDeLogin = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/entrar',
  beforeLoad: () => {
    if (usePortalAuth.getState().situacao === 'autenticado') throw redirect({ to: '/' });
  },
  component: LoginPage,
});

/**
 * O guarda de sessão.
 *
 * Mandar para `/entrar` sem `?destino=` é deliberado: o destino chega pela URL
 * e é entrada do usuário, e a tela de login da operação precisou de uma
 * conferência de caminho interno justamente para não virar redirecionador
 * aberto. O portal está na internet aberta e não tem nenhum fluxo que dependa
 * de voltar ao ponto exato — não construir a funcionalidade é mais barato que
 * construí-la segura.
 */
const rotaAutenticada = createRoute({
  getParentRoute: () => rotaRaiz,
  id: 'autenticado',
  beforeLoad: () => {
    if (usePortalAuth.getState().situacao !== 'autenticado') throw redirect({ to: '/entrar' });
  },
  component: () => (
    <PortalShell>
      <Outlet />
    </PortalShell>
  ),
});

const rotaDeChamados = createRoute({
  getParentRoute: () => rotaAutenticada,
  path: '/',
  component: ChamadosPage,
});

const rotaDeChamado = createRoute({
  getParentRoute: () => rotaAutenticada,
  path: '/chamados/$id',
  component: function Chamado() {
    const { id } = rotaDeChamado.useParams();
    return <ChamadoPage id={id} />;
  },
});

const rotaDeAbertura = createRoute({
  getParentRoute: () => rotaAutenticada,
  path: '/abrir',
  component: AbrirChamadoPage,
});

const rotaDaBase = createRoute({
  getParentRoute: () => rotaAutenticada,
  path: '/base',
  component: BasePage,
});

const rotaDeArtigo = createRoute({
  getParentRoute: () => rotaAutenticada,
  path: '/base/$id',
  component: function Artigo() {
    const { id } = rotaDeArtigo.useParams();
    return <ArtigoPage id={id} />;
  },
});

const arvore = rotaRaiz.addChildren([
  rotaDeLogin,
  rotaAutenticada.addChildren([
    rotaDeChamados,
    rotaDeChamado,
    rotaDeAbertura,
    rotaDaBase,
    rotaDeArtigo,
  ]),
]);

export const roteadorDoPortal = createRouter({ routeTree: arvore });

/**
 * O registro do roteador — e por que ele obrigou dois `tsconfig`.
 *
 * `interface Register` é **global ao programa TypeScript**. Com o roteador da
 * operação e o do portal no mesmo programa, o segundo registro sobrescreve o
 * primeiro, e todo `<Link to="/abrir">` daqui passa a ser conferido contra as
 * rotas de lá — `Type '"/abrir"' is not assignable to '"/chamados" | …'`, um
 * erro que aponta para a linha errada e não sugere a causa.
 *
 * A saída foi separar os programas: `tsconfig.json` exclui `src/portal`, e
 * `tsconfig.portal.json` inclui só ele. O efeito colateral é o que se queria
 * de qualquer forma — o compilador do portal não enxerga `src/app` nem
 * `src/features`, e a fronteira do §c.2 deixa de depender de disciplina.
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof roteadorDoPortal;
  }
}
